//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useDebounce } from 'use-debounce';

import {
  BrandSortCriteria,
  SamplingBrandsResponse,
} from '@apis/campaign-requests/sampling/products/samplingBrandsApiModel';
import { useGetSamplingBrandsQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { TypeaheadSearchField } from '@campaigns/components/shared/product-details-modal/form/TypeaheadSearchField';
import { ProductDetailsFieldLabels } from '@campaigns/constants/ProductDetailsModalFieldLabels';
import { PRODUCT_MODAL_FIELD_IDS } from '@campaigns/constants/ProductModalFieldIds';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import { SAMPLING_CAMPAIGN_PRODUCT_MODAL_BRAND_SEARCH_PREFIX } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { formatProductCatalogSearch } from '@campaigns/utils/formatProductCatalogSearch';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';

const MAX_BRAND_RESULTS = 10;

interface BrandOption {
  externalID: string;
  name: string;
}

interface BrandSearchFieldProps {
  error?: FieldError;
  onSelectBrand: (brandName: string) => void;
  value?: string;
}

const selectBrandOptions = (data: SamplingBrandsResponse): BrandOption[] => {
  if (!data.brands) {
    return [];
  }
  return data.brands.map((brand) => ({
    externalID: brand.externalID ?? '',
    name: brand.name ?? DEFAULT_EMPTY_VALUE,
  }));
};

export const BrandSearchField = ({
  error,
  onSelectBrand,
  value = '',
}: BrandSearchFieldProps) => {
  const { selectedRetailer } = useSamplingCampaign();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  const formattedSearch = formatProductCatalogSearch(
    debouncedSearchTerm,
    'name',
  );

  const {
    data: brandOptions = [],
    error: queryError,
    isLoading,
  } = useGetSamplingBrandsQuery<BrandOption[]>(
    {
      active: true,
      limit: MAX_BRAND_RESULTS,
      retailerId: selectedRetailer?.clientId,
      searchText: formattedSearch,
      sortCriteria: BrandSortCriteria.Name,
    },
    selectBrandOptions,
  );

  return (
    <TypeaheadSearchField
      debouncedSearchTerm={debouncedSearchTerm}
      error={error}
      fieldId={PRODUCT_MODAL_FIELD_IDS.BRAND}
      fieldLabelId={PRODUCT_MODAL_FIELD_IDS.BRAND_LABEL}
      helpText='Enter the brand to which the product belongs.'
      isLoading={isLoading}
      labelText={ProductDetailsFieldLabels.brand}
      onSearch={setSearchTerm}
      onSelect={onSelectBrand}
      options={brandOptions}
      placeholderText=''
      queryError={queryError}
      resourceName='brands'
      searchPlaceholder='Search brands'
      trackingIdPrefix={SAMPLING_CAMPAIGN_PRODUCT_MODAL_BRAND_SEARCH_PREFIX}
      value={value}
      visuallyHiddenLabel='Search brands'
    />
  );
};
